import { useState } from "react";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import DownloadIcon from "@mui/icons-material/Download";

interface CsvExportButtonProps {
  /** Builds and downloads the file; a rejection is already surfaced by the axios interceptor. */
  onExport: () => Promise<void> | void;
  disabled?: boolean;
  label?: string;
}

/**
 * The shared ExportButton downloads a server-built file; this screen has no export endpoint,
 * so it builds a UTF-8 (BOM) CSV in the browser from the same API the table reads. Same look
 * and label as the other list exports.
 */
export default function CsvExportButton({
  onExport,
  disabled = false,
  label = "Export Excel",
}: Readonly<CsvExportButtonProps>) {
  const [exporting, setExporting] = useState(false);

  const handleClick = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      await onExport();
    } catch {
      // Already surfaced by the axios interceptor.
    } finally {
      setExporting(false);
    }
  };

  return (
    <Button
      variant="outlined"
      startIcon={exporting ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon />}
      onClick={handleClick}
      disabled={disabled || exporting}
      sx={{ whiteSpace: "nowrap" }}
    >
      {label}
    </Button>
  );
}
